import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import TablePagination from "../../../components/TablePagination";
import api from "../../../services/api";
import { authenticationService } from "../../../services/auth.service";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import toastOptions from "../../../utils/toastOptions";
import {
  faMoneyBill,
  faPencil,
  faPrint,
  faSearch,
  faTrash,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  currencyMask,
  displayDate2,
  displayDateTime,
} from "../../../utils/masks";
import EventPayment from "../event-payment/EventPayment";
import IPersonDTO from "../../../models/IPersonDTO";
import DialogConfirm from "../../../components/DialogConfirm";
import { utils } from "../../../utils/utils";
import Param from "../../../models/Param";
import IEventDetail from "../../../models/IEventDetail";

interface PersonListDTO {
  id: number;
  name: string;
  createdDate: Date;
  isIndividual: boolean;
  paidDate: Date;
  congregation: string;
}

type RouteParams = {
  id: string;
};

export default function EventPersonList() {
  const params = useParams<RouteParams>();
  const [isReadOnly, setReadOnly] = useState<boolean>();
  const [termSearch, setTermSearch] = useState<string>("");
  const [refresh, setRefresh] = useState<boolean>();
  const [isDialogDelete, setIsDialogDelete] = useState<boolean>(false);
  const [dialogDeleteText, setDialogDeleteText] = useState<string[]>([]);
  var personSelected = useRef(0);
  const [delayedSearchTerm, setDelayedSearchTerm] = useState("");
  const [open, setOpen] = useState<boolean>(false);
  const [addPayment, setAddPayment] = useState<boolean>(false);
  const [person, setPerson] = useState<IPersonDTO>();
  const [totalEnrollments, setTotalEnrollments] = useState<number | undefined>(undefined);
  const [totalPaid, setTotalPaid] = useState<number | undefined>(undefined);
  const [totalCouple, setTotalCouple] = useState<number | undefined>(undefined);
  const [totalIndividual, setTotalIndividual] = useState<number | undefined>(undefined);
  const [totalPeople, setTotalPeople] = useState<number | undefined>(undefined);
  const [ixSend, setIxSent] = useState<number>(-1);
  const [printingReport, setPrintingReport] = useState<number>(-1);
  const [congregations, setCongregations] = useState<Param[]>([]);
  const [congSearch, setCongSearch] = useState<number>(-1);
  const [sumPayments, setSumPayments] = useState<number | undefined>(undefined);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [hasActiveFilter, setHasActiveFilter] = useState<boolean>(false);

  const columns = [
    {
      name: "Nome",
      sortable: true,
      grow: 2,
      cell: (row: PersonListDTO) => (
        <div className="uk-flex uk-flex-column uk-margin-xsmall-y">
          <label>
            {row.name}{" "}
            {congSearch <= 0 && row.congregation && (
              <label className="uk-text-light uk-text-italic">
                ({row.congregation})
              </label>
            )}
          </label>
          <div>
            {row.isIndividual ? (
              <span className="uk-badge uk-badge-success">Individual</span>
            ) : (
              <span className="uk-badge uk-badge-default">Casal</span>
            )}
          </div>
        </div>
      )
    },
    {
      name: "Info",
      cell: (row: PersonListDTO) => (
        <div className="uk-flex uk-flex-column uk-text-xsmall">
          <span className="uk-text-meta uk-text-italic uk-text-xsmall">
            Inscrição feita em {displayDateTime(row.createdDate)}
          </span>
          {row.paidDate && (
            <span className="uk-text-success uk-text-bold">
              Pago em {displayDate2(row.paidDate)}
            </span>
          )}
        </div>
      )
    },
    {
      name: "Ações",
      button: true,
      sortable: false,
      cell: (row: IPersonDTO, index: number) => (
        <div className="uk-flex">
          {isReadOnly ? (
            <Link
              to={`/eventos/${params.id}/pessoas/${row.id}`}
              uk-tooltip="title: Visualizar"
              className="uk-margin-small-right"
            >
              <FontAwesomeIcon icon={faSearch} />
            </Link>
          ) : (
            <Link
              to={`/eventos/${params.id}/pessoas/${row.id}`}
              uk-tooltip="title: Editar"
              className="uk-margin-small-right"
            >
              <FontAwesomeIcon icon={faPencil} />
            </Link>
          )}
          {authenticationService.isFinance() && !row.paidDate &&
            <a
              href="#"
              uk-tooltip="title: Pagamento"
              className="uk-margin-small-right"
              onClick={(ev) => {
                ev.preventDefault();
                handlePayment(row);
              }}
            >
              {ixSend == row.id ? (
                <div uk-spinner="true; ratio:0.5"></div>
              ) : (
                <FontAwesomeIcon icon={faMoneyBill} />
              )}
            </a>
          }
          {authenticationService.isFinance() && !row.paidDate && (
            <a
              href="#"
              uk-tooltip="title: Lembrete Pagamento"
              className="uk-margin-small-right"
              onClick={(ev) => {
                ev.preventDefault();
                handleEmailPayment(row, index);
              }}
            >
              <FontAwesomeIcon icon={faTriangleExclamation} />
            </a>
          )}
          {!isReadOnly && (
            <a
              href="#"
              uk-tooltip="title: Remover"
              className="uk-margin-small-right"
              onClick={(ev) => {
                ev.preventDefault();
                handleRemove(row);
              }}
            >
              {ixSend == row.id ? <div uk-spinner="true; ratio:0.5"></div> : <FontAwesomeIcon icon={faTrash} />}
            </a>
          )}
        </div>
      ),
    },
  ];

  useEffect(() => {
    setReadOnly(!authenticationService.canChange());
    countStatistics();
  }, []);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      setDelayedSearchTerm(termSearch);
    }, 500); // You can adjust the delay time according to your preferences

    return () => clearTimeout(delaySearch);
  }, [termSearch]);

  useEffect(() => {
    // Perform the search operation with delayedSearchTerm
    // This will be called only when the user stops typing for the specified delay time
    setRefresh(!refresh);
  }, [delayedSearchTerm]);

  useEffect(() => {
    setHasActiveFilter(open == true || congSearch >= 0 || delayedSearchTerm?.trim().length > 0);
  }, [open, congSearch, delayedSearchTerm]);

  const countStatistics = async () => {
    const { data: dataCongregations } = await api.get("congregations/dashboard",);
    const { data: enrollments } = await api.get(`events/${params.id}/count/enrollments`,);
    const { data: paids } = await api.get(`events/${params.id}/count/paids`);
    const { data: couple } = await api.get(`events/${params.id}/count/couple`);
    const { data: individual } = await api.get(`events/${params.id}/count/individual`);
    const { data: people } = await api.get(`events/${params.id}/count/people`);

    if (authenticationService.isFinance()) {
      const { data: dataSum } = await api.get(`events/${params.id}/sum-payments`);
      setSumPayments(dataSum);
    }

    setTotalEnrollments(enrollments);
    setTotalPaid(paids);
    setTotalCouple(couple);
    setTotalIndividual(individual);
    setTotalPeople(people);
    setCongregations(dataCongregations);
  };

  const fetchPeople = async (page: number, limit: number) => {
    try {
      let requestParams = {
        page: page,
        limit: limit,
        open: open,
        congregation: congSearch,
      } as any;

      if (delayedSearchTerm) {
        requestParams = { ...requestParams, name: delayedSearchTerm.toUpperCase() };
      }

      const response = await api.get(`events/${params.id}/people/`, {
        params: requestParams,
      });

      setTotalRecords(response?.data?.total ?? 0);

      return response;
    } catch {
      setTotalRecords(0);
      return { data: [], total: 0 };
    }
  };

  const handleRemove = async (row: IPersonDTO) => {
    setDialogDeleteText([`Deseja realmente remover ${row.name}?`]);
    personSelected.current = row.id;
    setIsDialogDelete(true);
  };

  const dialogClose = () => {
    setIsDialogDelete(false);
  };

  const handleSearchByName = async (search: string) => {
    if (search.length > 2 || search.length === 0) {
      setTermSearch(search);
    }
  };

  const handleOpen = async (selected: boolean) => {
    setOpen(selected);
    setRefresh(!refresh);
  };

  const handleChurch = async (congregation: number) => {
    setCongSearch(congregation);
    setRefresh(!refresh);
  };

  const handlePayment = async (row: IPersonDTO) => {
    setPerson(row);
    setAddPayment(true);
  };

  const afterSubmitPayment = (paidDate: Date) => {
    if (person) {
      person.paidDate = paidDate;
      countStatistics();
    }
    afterCloseModal();
  };

  const afterCloseModal = () => {
    setAddPayment(false);
    setPerson({} as IPersonDTO);
  };

  const dialogConfirm = async () => {
    try {
      setIsDialogDelete(false);
      setIxSent(personSelected.current);
      await api.delete(`/events/${params.id}/person/${personSelected.current}`);
      toast.success("Pessoa removida com sucesso!", toastOptions);
      setRefresh(!refresh);
      countStatistics();
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    } finally {
      setIxSent(-1);
    }
  };

  const handleEmailPayment = async (row: IPersonDTO, ix: number) => {
    try {
      setIxSent(ix);
      await api.post(`/events/people/${row.id}/email/pending`);
      toast.success(`Cobrança enviada para ${row.name} com sucesso!`, toastOptions);
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    } finally {
      setIxSent(-1);
    }
  };

  const handlePersonByCongReport = async () => {
    try {
      setPrintingReport(0);
      const { data } = await api.get(
        `/events/${params.id}/report/congregation`,
      );
      const name = `relatório-por-congregação-${new Date().getTime()}.pdf`;
      utils.printPDF(name, data.item);
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    } finally {
      setPrintingReport(-1);
    }
  };

  return (
    <div id="page-event-person">
      <div className="uk-card uk-card-default uk-card-small uk-card-hover uk-margin-bottom">
        <div className="uk-card-header">
          <div className="uk-grid-small uk-flex-middle" uk-grid="true">
            <div className="uk-width-expand">
              <h3 className="uk-card-title uk-margin-remove-bottom">
                Inscrições Ativas
              </h3>
            </div>
            <Link
              className="uk-button uk-button-primary uk-button-small"
              to={`/eventos/${params.id}/dashboard`}
            >
              Voltar
            </Link>
          </div>
        </div>
        <div className="uk-card-body">
          <div className="uk-flex@m uk-flex-middle">
            <button
              className="uk-button uk-button-primary uk-button-small uk-margin-small-right"
              type="button"
            >
              Inscrições:{" "}
              {totalEnrollments == undefined ? (
                <div uk-spinner="true; ratio:0.5"></div>
              ) : (
                totalEnrollments
              )}
            </button>
            <button
              className="uk-button uk-button-success uk-button-small uk-margin-small-right"
              type="button"
            >
              Pagas:{" "}
              {totalPaid == undefined ? (
                <div uk-spinner="true; ratio:0.5"></div>
              ) : (
                totalPaid
              )}
            </button>
            <button
              className="uk-button uk-button-danger uk-button-small uk-margin-small-right"
              type="button"
            >
              Casais:{" "}
              {totalCouple == undefined ? (
                <div uk-spinner="true; ratio:0.5"></div>
              ) : (
                totalCouple
              )}
            </button>
            <button
              className="uk-button uk-button-secondary uk-button-small uk-margin-small-right"
              type="button"
            >
              Individuais:{" "}
              {totalIndividual == undefined ? (
                <div uk-spinner="true; ratio:0.5"></div>
              ) : (
                totalIndividual
              )}
            </button>
            <button
              className="uk-button uk-button-warning uk-button-small"
              type="button"
            >
              Qtd. Pessoas:{" "}
              {totalPeople == undefined ? (
                <div uk-spinner="true; ratio:0.5"></div>
              ) : (
                totalPeople
              )}
            </button>
          </div>
          <div className="uk-flex@m uk-flex-middle uk-margin-top">
            <button
              className="uk-button uk-button-default uk-button-small uk-margin-small-right"
              type="button"
              onClick={() => handlePersonByCongReport()}
              disabled={+(params?.id ?? 0) < 1 || totalPeople == undefined}
            >
              Inscrições por Congregação
              {printingReport == 0 ? (
                <div
                  className="uk-margin-small-left"
                  uk-spinner="true; ratio:0.5"
                ></div>
              ) : (
                <FontAwesomeIcon
                  className="uk-margin-small-left"
                  icon={faPrint}
                />
              )}
            </button>
            {authenticationService.isFinance() && (
              <button
                className="uk-button uk-button-success uk-button-small"
                type="button"
                disabled={+(params?.id ?? 0) < 1 || totalPeople == undefined}
              >
                Pago:{" "}
                {sumPayments == undefined ? (
                  <div uk-spinner="true; ratio:0.5"></div>
                ) : (
                  currencyMask(sumPayments)
                )}
              </button>
            )}
          </div>
          <div className="uk-grid-small uk-margin-top" uk-grid="true">
            <div className="uk-search uk-search-default uk-width-1-3@m">
              <span
                className="uk-search-icon-flip"
                uk-search-icon="true"
              ></span>
              <input
                className="uk-search-input uk-input uk-form-small"
                type="search"
                placeholder="Pesquise por nome..."
                style={{ textTransform: "uppercase" }}
                onChange={(ev) => {
                  handleSearchByName(ev.target.value);
                }}
              />
            </div>
            <div className="uk-flex uk-flex-middle uk-width-1-3@m">
              <label
                className="uk-form-label uk-margin-right"
                htmlFor="congregationId"
              >
                Igreja:
              </label>
              <div
                uk-form-custom="target: > * > span:first-child"
                className="uk-flex-1"
              >
                <select
                  className="uk-form-small"
                  id="congregationId"
                  name="congregationId"
                  value={congSearch}
                  onChange={(e) => handleChurch(+e.target.value)}
                >
                  <option value="-1" key={`congregation-1`}>
                    Todas
                  </option>
                  {congregations.map((f) => {
                    return (
                      <option value={f.id} key={`congregation${f.id}`}>
                        {f.description}
                      </option>
                    );
                  })}
                  <option value="0" key={`congregation0`}>
                    Outro Ministério
                  </option>
                </select>
                <button
                  className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                  style={{ textTransform: "none" }}
                  type="button"
                  tabIndex={-1}
                >
                  <span></span>
                  <span uk-icon="icon: chevron-down"></span>
                </button>
              </div>
            </div>
            <div className="uk-width-1-3@m uk-flex uk-flex-middle">
              <label
                className="uk-form-label uk-margin-small-right"
                htmlFor="type"
              >
                Inscrições não Pagas?
              </label>
              <input
                className="uk-checkbox uk-margin-left"
                type="checkbox"
                id="open"
                checked={open}
                onChange={(e) => {
                  handleOpen(e.target.checked);
                }}
              />
            </div>
            {hasActiveFilter && (
              <div className="uk-width-1-1 uk-flex uk-flex-start">
                <span className="uk-badge uk-padding-small">
                  Filtro = {totalRecords} inscriç{totalRecords != 1 ? "ões" : "ão"}
                </span>
              </div>
            )}
          </div>
          <TablePagination
            columns={columns}
            fetchData={fetchPeople}
            refresh={refresh}
          />
        </div>
      </div>
      <EventPayment
        add={addPayment}
        person={person}
        afterHandleSubmit={afterSubmitPayment}
        afterCloseModal={afterCloseModal}
      />
      <DialogConfirm
        open={isDialogDelete}
        text={dialogDeleteText}
        afterClose={dialogClose}
        handleConfirmation={(confirmationCode) => dialogConfirm()}
      />
    </div>
  );
}
